import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  BadgeComponent,
  EMPLOYEE_ROUTES,
  FilteredTableComponent,
  FilteredTablePayload,
  Meta,
  TableFilterTab,
  TenantService,
  injectFloatingLayout,
  injectFloatingPageClasses,
  provideSystemTabsLayout,
} from '@kuiper/ibusiness-core';
import { BackgroundTaskService } from './background-task.service';
import {
  BackgroundTask,
  BackgroundTaskProgress,
  BackgroundTasksResponse,
} from './background-task.model';
import {
  BACKGROUND_TASK_COLUMNS,
  BACKGROUND_TASK_DEFAULT_PAYLOAD,
  BACKGROUND_TASK_FILTER_TABS,
  STATE_BADGE,
  STATES_BY_TAB,
} from './background-task-list.constants';

/**
 * The user's own background tasks (imports), newest first, each opening its result screen.
 */
@Component({
  selector: 'app-background-task-list',
  imports: [BadgeComponent, DatePipe, FilteredTableComponent, TranslocoPipe],
  providers: [provideSystemTabsLayout()],
  templateUrl: './background-task-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BackgroundTaskListComponent implements OnInit {
  protected readonly floating = injectFloatingLayout();
  protected readonly systemTabsPageClasses = injectFloatingPageClasses();
  private readonly service = inject(BackgroundTaskService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly backgroundTasksPath = inject(TenantService).tenantPath(
    EMPLOYEE_ROUTES.BACKGROUND_TASKS
  );

  readonly tasks = signal<BackgroundTask[]>([]);
  readonly meta = signal<Meta | undefined>(undefined);
  readonly isLoading = signal(false);

  readonly tableFilterTabs = signal<TableFilterTab[]>(
    BACKGROUND_TASK_FILTER_TABS.map(tab => ({
      ...tab,
      label: this.transloco.translate(tab.label),
    }))
  );

  readonly payload = signal<FilteredTablePayload>({
    ...BACKGROUND_TASK_DEFAULT_PAYLOAD,
    sortColumn: { ...BACKGROUND_TASK_DEFAULT_PAYLOAD.sortColumn },
    tab: this.tableFilterTabs()[0],
  });

  readonly columns = computed(() =>
    BACKGROUND_TASK_COLUMNS.map(column => ({
      ...column,
      header: column.header ? this.transloco.translate(column.header) : '',
      viewIconRouterLink: column.showViewIcon
        ? (task: BackgroundTask) => [this.backgroundTasksPath, `${task.id}`]
        : undefined,
    }))
  );

  readonly rows = computed(() =>
    this.tasks().map(task => ({
      ...task,
      badgeClass: `px-sm border-none ${STATE_BADGE[task.state.value] ?? STATE_BADGE['pending']}`,
      created: task.output?.created ?? '-',
      skipped: task.output?.skipped ?? '-',
    }))
  );

  readonly loadTasks$ = () => {
    const { page, size, sortColumn, tab } = this.payload();
    this.isLoading.set(true);

    return this.service
      .list(page, size, STATES_BY_TAB[tab.key] ?? [], sortColumn)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      );
  };

  ngOnInit(): void {
    this.service
      .progress()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => this.applyProgress(event));
  }

  loadTasks(): void {
    this.loadTasks$().subscribe({
      next: response => this.applyTasks(response),
      error: () => this.clearTasks(),
    });
  }

  applyTasks(response: BackgroundTasksResponse): void {
    this.tasks.set(response.data ?? []);
    this.meta.set(response.meta);
  }

  clearTasks(): void {
    this.tasks.set([]);
    this.meta.set(undefined);
  }

  /**
   * A listed task that changes state is loaded again for its new label and counts; a (re)joined channel (`null`)
   * reloads the page, since events sent before the join are lost.
   */
  private applyProgress(event: BackgroundTaskProgress | null): void {
    if (event === null) {
      this.loadTasks();

      return;
    }

    const task = this.tasks().find(item => item.id === event.id);

    if (!task || task.state.value === event.state) {
      return;
    }

    this.service
      .show(event.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(updated =>
        this.tasks.update(tasks =>
          tasks.map(item => (item.id === updated.id ? updated : item))
        )
      );
  }
}
